import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { T3ApiResponse, T3Year } from './t3-response-model';

export interface T3AnalysisRun {
    id: string;
    title: string;
    createdAt: number;
    t3: T3ApiResponse;
    isBaseline?: boolean;
    params?: any;
}

const T3_RUNS_KEY = 't3_runs_v1';
const T3_SELECTED_KEY = 't3_selected_run_id_v1';

@Injectable({ providedIn: 'root' })
export class T3AnalysisStore {
    private runsListSubject = new BehaviorSubject<T3AnalysisRun[]>([]);
    runsList$ = this.runsListSubject.asObservable();

    private selectedRunIdSubject = new BehaviorSubject<string | null>(null);
    selectedRunId$ = this.selectedRunIdSubject.asObservable();

    constructor() {
        const runs = this.loadRunsFromStorage();
        if (runs.length) {
            this.runsListSubject.next(runs);
        }

        const selected = this.loadSelectedRunId();
        if (selected) {
            this.selectedRunIdSubject.next(selected);
        } else if (runs.length) {
            const baseline = runs.find((r) => this.isBaselineTitle(r.title));
            this.selectedRunIdSubject.next((baseline ?? runs[0]).id);
        }

        this.selectedRunIdSubject.subscribe((id) => {
            try {
                if (id) localStorage.setItem(T3_SELECTED_KEY, id);
                else localStorage.removeItem(T3_SELECTED_KEY);
            } catch { }
        });
    }

    setSelectedRunId(id: string | null): void {
        this.selectedRunIdSubject.next(id);
    }

    getSelectedRunId(): string | null {
        return this.selectedRunIdSubject.value;
    }

    getRunsSnapshot(): T3AnalysisRun[] {
        return this.runsListSubject.value ?? [];
    }

    getRunByTitle(title: string): T3AnalysisRun | null {
        const t = this.norm(title);
        return this.getRunsSnapshot().find((r) => this.norm(r.title) === t) ?? null;
    }

    getRunById(id: string): T3AnalysisRun | null {
        return this.getRunsSnapshot().find((r) => String(r.id) === String(id)) ?? null;
    }

    getBaselineOrFirst(): T3AnalysisRun | null {
        const runs = this.getRunsSnapshot();
        if (!runs.length) return null;
        return runs.find((r) => this.isBaselineTitle(r.title)) ?? runs[0];
    }

    setBaselineRun(t3: T3ApiResponse, createdAt = Date.now()): void {
        const previousRuns = this.getRunsSnapshot();

        const existingBaseline = previousRuns.find((run) => this.isBaselineTitle(run.title));
        const baselineId = existingBaseline?.id || 'baseline';

        const baseline: T3AnalysisRun = {
            id: baselineId,
            title: 'Basic Simulation',
            createdAt,
            t3,
            isBaseline: true,
            params: existingBaseline?.params
        };

        const nonBaselineRuns = previousRuns.filter((run) => !this.isBaselineTitle(run.title));
        const nextRuns = [baseline, ...nonBaselineRuns];

        this.runsListSubject.next(nextRuns);
        this.persistRuns(nextRuns);

        const currentSelectedId = this.selectedRunIdSubject.value;
        const selectedStillExists =
            !!currentSelectedId && nextRuns.some((run) => run.id === currentSelectedId);

        this.selectedRunIdSubject.next(selectedStillExists ? currentSelectedId : baseline.id);
    }

    upsertRun(t3: T3ApiResponse, title: string, params?: any, createdAt = Date.now()): void {
        const normalizedTitle = (title || '').trim();
        if (!normalizedTitle) return;

        const prev = this.getRunsSnapshot();
        const idx = prev.findIndex((r) => this.norm(r.title) === this.norm(normalizedTitle));
        const isBaseline = this.isBaselineTitle(normalizedTitle);

        if (idx >= 0) {
            const next = [...prev];
            next[idx] = {
                ...next[idx],
                title: isBaseline ? 'Basic Simulation' : normalizedTitle,
                t3,
                params,
                createdAt
            };

            this.runsListSubject.next(next);
            this.persistRuns(next);
            this.selectedRunIdSubject.next(next[idx].id);
            return;
        }

        const run: T3AnalysisRun = {
            id: String(this.nextId(prev)),
            title: isBaseline ? 'Basic Simulation' : normalizedTitle,
            createdAt,
            t3,
            params,
            isBaseline
        };

        const next = [...prev, run];
        this.runsListSubject.next(next);
        this.persistRuns(next);
        this.selectedRunIdSubject.next(run.id);
    }

    renameRunTitle(oldTitle: string, newTitle: string): void {
        const oldNorm = this.norm(oldTitle);
        const newTrim = (newTitle || '').trim();

        if (!oldNorm || !newTrim) return;

        const exists = this.getRunsSnapshot().some((r) => this.norm(r.title) === this.norm(newTrim));
        if (exists) return;

        const next = this.getRunsSnapshot().map((r) =>
            this.norm(r.title) === oldNorm ? { ...r, title: newTrim } : r
        );

        this.runsListSubject.next(next);
        this.persistRuns(next);
    }

    deleteRunsByTitle(titles: string[]): void {
        const deleteSet = new Set((titles || []).map((x) => this.norm(x)));
        const next = this.getRunsSnapshot().filter((r) => !deleteSet.has(this.norm(r.title)));

        this.runsListSubject.next(next);
        this.persistRuns(next);

        const current = this.selectedRunIdSubject.value;
        if (current && !next.some((r) => r.id === current)) {
            const baseline = next.find((r) => this.isBaselineTitle(r.title));
            this.selectedRunIdSubject.next((baseline ?? next[next.length - 1] ?? null)?.id ?? null);
        }
    }

    clearAllRuns(): void {
        this.runsListSubject.next([]);
        this.selectedRunIdSubject.next(null);

        try {
            localStorage.removeItem(T3_RUNS_KEY);
            localStorage.removeItem(T3_SELECTED_KEY);
        } catch { }
    }

    pick(t3: T3ApiResponse, key: string, year: T3Year | null): number {
        if (!t3 || year === null) return 0;

        const series = (t3 as any)[key] as Record<string | number, number> | undefined;
        const raw = series ? (series as any)[year] : 0;
        const n = Number(raw);

        return Number.isFinite(n) ? Math.max(0, n) : 0;
    }

    pickNearestOrLatestYear(t3: T3ApiResponse, desiredYear: number): T3Year | null {
        const years = Array.isArray(t3?.years) ? t3.years.map(Number).filter(Number.isFinite) : [];

        if (!years.length) return null;
        if (years.includes(desiredYear)) return desiredYear;

        return years[years.length - 1];
    }

    loadRunsFromStorage(): T3AnalysisRun[] {
        try {
            const raw = localStorage.getItem(T3_RUNS_KEY);
            if (!raw) return [];

            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed as T3AnalysisRun[] : [];
        } catch {
            return [];
        }
    }

    private persistRuns(runs: T3AnalysisRun[]): void {
        try {
            localStorage.setItem(T3_RUNS_KEY, JSON.stringify(runs));
        } catch { }
    }

    private loadSelectedRunId(): string | null {
        try {
            return localStorage.getItem(T3_SELECTED_KEY);
        } catch {
            return null;
        }
    }

    private nextId(runs: T3AnalysisRun[]): number {
        const ids = runs.map((r) => Number(r.id)).filter(Number.isFinite);
        return ids.length ? Math.max(...ids) + 1 : 1;
    }

    private isBaselineTitle(title: string): boolean {
        const t = this.norm(title);
        return t === 'basic simulation' || t === 'baseline';
    }

    private norm(value: string): string {
        return String(value || '').trim().toLowerCase();
    }
}